<template>
  <div>
    <div class="board-wrapper">
      <div class="tictactoe-board">
        <div v-bind:key="i" v-for="(n, i) in 3">
          <div v-bind:key="j" v-for="(n, j) in 3">
            <Cell
              v-on:click="performMove(i, j)"
              v-bind:value="board.cells[i][j]"
              v-bind:gameOver="gameOver"
            ></Cell>
          </div>
        </div>
        <div class="score">
          <span
            >Scoring guide:<br />
            Win = 1 point<br />
            Draw = 0 points<br />
            Lose = -1 point
          </span>
          <span>Score: {{ totalScore }}</span>
          <button class="restart-button" v-on:click="restart">Restart</button>
        </div>
      </div>
      <div class="game-over-text" v-if="gameOver">
        {{ gameOverText }}
      </div>
    </div>
  </div>
</template>

<script>
import Cell from "./cell";
import apiClientCreator from "../../../../helpers/api/request";

/**
 * The default tic-tac-toe game class
 */
export default {
  components: { Cell },
  data() {
    return {
      gameOver: false,
      gameOverText: "",
      board: new Board(),
      // For randomization purposes of the computer for the first move
      firstMove: true,
      // Need to fetch the current score of the current user from the db
      totalScore: 0
    };
  },
  methods: {
    /**
     * Performs a move on the board(2 dimensional array)
     * @param x The x coordinate on the board
     * @param y The y coordinate on the board
     */
    performMove(x, y) {
      if (!this.board.doMove(x, y, "x")) {
        // Invalid move.
        return;
      }
      // Force update in order to refresh the view
      this.$forceUpdate();
      if (this.board.isGameOver()) {
        this.gameOver = true;
        if (this.board.playerHas3InARow("x")) {
          this.gameOverText = "You win!";
          this.totalScore += 1;
          this.saveScore(this.totalScore);
          this.$emit('game-over', this.totalScore);
        } else {
          this.gameOverText = "Draw!";
          this.$emit('game-over', this.totalScore);
        }
        return;
      }
      let aiMove;
      // This happens only on the first turn of every game in order to randomize the moves of the computer
      if (this.firstMove) {
        let moves = this.board.getPossibleMoves();
        let randomMove = moves[Math.floor(Math.random() * moves.length)];
        // Assignment happens the same way as if the result was from the recursive function
        aiMove = {
          move: randomMove
        };
        this.firstMove = false;
        // All others times, the computer calculates the best possible option
      } else {
        // After the player makes his move, the computer calculates the best option
        // to make his move
        aiMove = this.minimax(this.board.copyBoard(), "o");
      }
      // The computer makes his move based on the calculations
      this.board.doMove(aiMove.move.x, aiMove.move.y, "o");
      if (this.board.isGameOver()) {
        this.gameOver = true;
        if (this.board.playerHas3InARow("o")) {
          this.gameOverText = "You lose!";
          this.totalScore -= 1;
          this.saveScore(this.totalScore);
        } else {
          this.gameOverText = "Draw!";
        }
        this.$emit('game-over', Math.max(0, this.totalScore));
      }
      // Force update in order to refresh the view
      this.$forceUpdate();
    },

    /**
     * Saves the total score of the current user onto the DB
     * @param score {number} the score we wish to save into the DB
     */
    saveScore(score) {
      apiClientCreator(`users/score`)
        .post("", { score: score, game: "TIC" }) // user id?
        .then(res => {})
        .catch(err => {});
    },

    /**
     * Recursive algorithm which determines which is the best possible move for the computer
     * @param board the current board
     * @param player
     * @param depth
     * @returns {{score: number, move: null}}
     */
    minimax(board, player, depth = 1) {
      // If the board has 3 in a row return the score of the board.
      if (board.isGameOver()) {
        return {
          score: board.getScore() + depth,
          move: null
        };
      }

      // The 'o' player wants to maximize its score, the 'x' player wants to
      // minimize its score
      let bestScore = player === "o" ? -10000 : 10000;
      let bestMove = null;

      // loop through all the possible moves
      for (let move of board.getPossibleMoves()) {
        // Get a copy of he current board and perform a move on it
        let newBoard = board.copyBoard();
        newBoard.doMove(move.x, move.y, player);

        // Recursively call the minimax function for the new board.
        const score = this.minimax(
          newBoard,
          player === "x" ? "o" : "x",
          depth + 1
        ).score;

        // If the score is better than the best saved score update the best saved
        // score to this move.
        if (
          (player === "o" && score > bestScore) ||
          (player === "x" && score < bestScore)
        ) {
          bestScore = score;
          bestMove = move;
        }
      }

      // Return the best found score & move!
      return {
        score: bestScore,
        move: bestMove
      };
    },
    /**
     * Restarts the game
     */
    restart() {
      this.gameOver = false;
      this.gameOverText = "";
      this.board = new Board();
      this.firstMove = true;
    }
  }
};

/**
 * The board class of the tic-tac-toe game, it's a 3x3 grid
 */
class Board {
  constructor() {
    this.cells = [["", "", ""], ["", "", ""], ["", "", ""]];
  }

  /**
   * @param x The x location
   * @param y The y location
   * @param player The player either 'x' or 'o'
   * @returns {boolean} True if successful, False if invalid move.
   */
  doMove(x, y, player) {
    if (this.cells[x][y] !== "") {
      return false;
    }

    this.cells[x][y] = player;
    return true;
  }

  /**
   * Evaluates how good the board is for the computer, in order to allow him to make the best move
   * @returns {number} the score of the game
   */
  getScore() {
    let score = 0;
    if (this.playerHas3InARow("x")) {
      score -= 100;
    }
    if (this.playerHas3InARow("o")) {
      score += 100;
    }
    return score;
  }

  /**
   * Checks if the player or the computer have 3 in a row resulting in a win
   * @param player the player that is currently playing ('x'-the player or 'o'-the computer)
   * @returns {boolean} True if the player that was passed in the parameters has 3 in a row
   */
  playerHas3InARow(player) {
    // Horizontal rows
    for (let i = 0; i < 3; i++) {
      if (
        this.cells[0][i] === player &&
        this.cells[1][i] === player &&
        this.cells[2][i] === player
      ) {
        return true;
      }
    }

    // Vertical rows
    for (let i = 0; i < 3; i++) {
      if (
        this.cells[i][0] === player &&
        this.cells[i][1] === player &&
        this.cells[i][2] === player
      ) {
        return true;
      }
    }

    // Diagonals
    if (
      this.cells[0][0] === player &&
      this.cells[1][1] === player &&
      this.cells[2][2] === player
    ) {
      return true;
    }
    if (
      this.cells[2][0] === player &&
      this.cells[1][1] === player &&
      this.cells[0][2] === player
    ) {
      return true;
    }
    return false;
  }

  /**
   * Checks if there is a winner or a draw on the board - hence the game is over
   * @returns {boolean} True if the game is over or False otherwise
   */
  isGameOver() {
    return (
      this.getPossibleMoves().length === 0 ||
      this.playerHas3InARow("x") ||
      this.playerHas3InARow("o")
    );
  }

  /**
   * Get all the possible moves that are available on the board
   * @returns {[]} all the indexes in the board where it is possible to do a move
   */
  getPossibleMoves() {
    let moves = [];
    // Loop through the current board
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (this.cells[i][j] === "") {
          // Insert the (x,y) coordinates of the board into the possible moves array
          moves.push({ x: i, y: j });
        }
      }
    }
    return moves;
  }

  /**
   * Returns a copy of the current board
   * @returns {Board} a copy of the current board
   */
  copyBoard() {
    let clone = new Board();
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        clone.cells[i][j] = this.cells[i][j];
      }
    }
    return clone;
  }
}
</script>

<style scoped>
.board-wrapper {
  padding: 5em 0;
  display: flex;
  flex-direction: column;
}
.tictactoe-board {
  display: flex;
  justify-content: center;
}
.score {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 0 1em;
  font-size: 1.5em;
}
.restart-button {
  border-radius: 14px;
  border-color: #0d6aad;
  background-color: #008cba;
  color: white;
  outline: none;
}
.game-over-text {
  font-weight: bold;
  color: black;
  font-size: 1.5em;
  text-align: center;
  padding-top: 2em;
}
</style>
